import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { notifyNewFollower } from './notificationsService';

/**
 * Seguir um usuário
 */
export const followUser = async (
  currentUserId: string,
  targetUserId: string
): Promise<void> => {
  if (currentUserId === targetUserId) {
    throw new Error('Você não pode seguir a si mesmo');
  }

  try {
    // Buscar informações do usuário atual para notificação
    const currentUserDoc = await getDoc(doc(db, 'users', currentUserId));
    const currentUserData = currentUserDoc.data();
    const currentUserName = currentUserData?.name || 'Alguém';
    const currentUserAvatar = currentUserData?.profilePictureUrl;

    // Atualizar lista de following do usuário atual
    const currentUserRef = doc(db, 'users', currentUserId);
    await updateDoc(currentUserRef, {
      following: arrayUnion(targetUserId),
    });

    // Atualizar lista de followers do usuário alvo
    const targetUserRef = doc(db, 'users', targetUserId);
    await updateDoc(targetUserRef, {
      followers: arrayUnion(currentUserId),
    });

    // Enviar notificação
    await notifyNewFollower(
      currentUserId,
      currentUserName,
      currentUserAvatar,
      targetUserId
    );
  } catch (error) {
    console.error('Erro ao seguir usuário:', error);
    throw error;
  }
};

/**
 * Deixar de seguir um usuário
 */
export const unfollowUser = async (
  currentUserId: string,
  targetUserId: string
): Promise<void> => {
  try {
    // Remover da lista de following do usuário atual
    const currentUserRef = doc(db, 'users', currentUserId);
    await updateDoc(currentUserRef, {
      following: arrayRemove(targetUserId),
    });

    // Remover da lista de followers do usuário alvo
    const targetUserRef = doc(db, 'users', targetUserId);
    await updateDoc(targetUserRef, {
      followers: arrayRemove(currentUserId),
    });
  } catch (error) {
    console.error('Erro ao deixar de seguir usuário:', error);
    throw error;
  }
};

/**
 * Verifica se o usuário atual está seguindo outro usuário
 */
export const isFollowing = async (
  currentUserId: string,
  targetUserId: string
): Promise<boolean> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', currentUserId));

    if (!userDoc.exists()) {
      return false;
    }

    const following = userDoc.data().following || [];
    return following.includes(targetUserId);
  } catch (error) {
    console.error('Erro ao verificar se está seguindo:', error);
    return false;
  }
};

/**
 * Pega lista de seguidores de um usuário
 */
export const getFollowers = async (userId: string): Promise<string[]> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      return [];
    }

    return userDoc.data().followers || [];
  } catch (error) {
    console.error('Erro ao buscar seguidores:', error);
    return [];
  }
};

/**
 * Pega lista de quem o usuário está seguindo
 */
export const getFollowing = async (userId: string): Promise<string[]> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      return [];
    }

    return userDoc.data().following || [];
  } catch (error) {
    console.error('Erro ao buscar quem está seguindo:', error);
    return [];
  }
};

/**
 * Pega contagem de seguidores e seguindo
 */
export const getFollowCounts = async (
  userId: string
): Promise<{ followers: number; following: number }> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));

    if (!userDoc.exists()) {
      return { followers: 0, following: 0 };
    }

    const data = userDoc.data();
    return {
      followers: (data.followers || []).length,
      following: (data.following || []).length,
    };
  } catch (error) {
    console.error('Erro ao buscar contagens:', error);
    return { followers: 0, following: 0 };
  }
};
